<template>
    <div class="s-side-menu-content">
        <!-- ================= TABS ================= -->
        <template v-if="type === 'tabs' && activeTabSlice">
            <component
                :is="getComponent(activeTabSlice.name)"
                v-bind="activeTabSlice.data"
                :key="activeTabId || activeTabSlice.name"
                :ref="activeTabId"
                :data-anchor-id="activeTabId"
            />
        </template>

        <!-- ================= LINKS ================= -->
        <template v-else-if="type === 'links'">
            <component
                :is="getComponent(slice.name)"
                v-for="slice in slices"
                v-bind="slice.data"
                :id="getSliceId(slice)"
                :key="getSliceId(slice)"
                :ref="getSliceId(slice)"
            />
        </template>

        <!-- ================= COMBINED ================= -->
        <template v-else-if="type === 'combined'">
            <div
                v-for="item in slices"
                :key="getSliceId(item)"
            >
                <template v-if="activeElement === getSliceId(item)">
                    <component
                        :is="getComponent(child.name)"
                        v-for="child in item.slices"
                        v-bind="child.data"
                        :id="getSliceId(child)"
                        :key="getSliceId(child)"
                        :ref="getSliceId(child)"
                    />
                </template>
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed, watchEffect } from 'vue';
import { registerSlices } from '@utils/dynamic-slices-resolver';

defineOptions({
    name: 'SSideMenuContent',
});

const props = defineProps({
    type: {
        type: String,
        default: 'tabs',
        validator: (value) => ['tabs', 'links', 'combined'].includes(value),
    },

    slices: {
        type: Array,
        default: () => [],
    },

    activeElement: {
        type: String,
        default: '',
    },
});

function normalizeId(id) {
    return String(id || '').replace(/^#/, '').trim();
}

function getSliceId(slice) {
    return normalizeId(slice?.info?.id);
}

const activeTabSlice = computed(() =>
    props.slices.find((x) => getSliceId(x) === props.activeElement),
);

const activeTabId = computed(() =>
    getSliceId(activeTabSlice.value),
);

const componentsRegistry = {};

function flattenSlices(items = []) {
    return items.flatMap((item) => {
        const current = item?.name ? [{ name: item.name, data: item.data }] : [];
        const children = Array.isArray(item?.slices) ? flattenSlices(item.slices) : [];
        return [...current, ...children];
    });
}

function resolveSlices() {
    const normalizedSlices = flattenSlices(props.slices);

    /*
        This code is required here, because we had occurances of incorrect JSON structure passed via admin panel
        The example was when s-compete-page passed to s-tabs via awardsTabs object instead of slices array
        Our dynamic resolver requires the slice to be passing "slices" array and having { data, name } inside
        Not "awardsTabs": { "slices": [{ data, name }] }
        Other example was s-documentation trough side-menu, so we should remove this code only if we resolve all such cases.
    */

    registerSlices({
        pageBody: normalizedSlices,
        componentRegistrar: (name, component) => {
            componentsRegistry[name] = component;
        },
    });
}

function getComponent(name) {
    const component = componentsRegistry[name];
    return component;
}

watchEffect(() => {
    resolveSlices();
});
</script>

<style lang="postcss">
.s-side-menu-content {
    @media (--viewport-desktop) {
        .s-slice:first-child {
            padding-top: 0;
        }
    }

    .a-container {
        padding: 0;
    }
}
</style>
